import { useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ShieldCheck, Loader2, Eye, EyeOff } from "lucide-react";
import { GoogleLogin } from "@react-oauth/google";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { toast } from "sonner";

export default function AuthModal({ open, onClose }) {
  const { login, register, checkAuth } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const GOOGLE_ENABLED = !!process.env.REACT_APP_GOOGLE_CLIENT_ID;

  const googleIdSuccess = async (credResponse) => {
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/google-id`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ credential: credResponse.credential }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
      await checkAuth();
      toast.success("Welcome to GuardGig28.");
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Welcome back.");
      } else {
        await register(name, email, password);
        toast.success("Account created. Welcome to GuardGig28.");
      }
      onClose();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
      setForgotSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "h-12 w-full rounded-md border border-white/10 bg-[#07090e] px-4 text-sm text-slate-50 placeholder:text-slate-600 focus:border-amber-500/60 focus:outline-none";

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="auth-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 24, scale: 0.97 }}
            animate={{ y: 0, scale: 1 }}
            exit={{ y: 24, scale: 0.97 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl border border-amber-500/25 bg-[#0f141c] p-8 shadow-2xl"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-amber-500 text-[#07090e]">
                  <ShieldCheck className="h-5 w-5" strokeWidth={2.5} />
                </span>
                <div>
                  <h2 className="font-display text-xl font-extrabold uppercase text-slate-50">
                    {mode === "forgot" ? "Reset password" : mode === "login" ? "Sign in" : "Create account"}
                  </h2>
                  <p className="font-mono-tag text-[9px] text-slate-500">GuardGig28 access</p>
                </div>
              </div>
              <button data-testid="auth-modal-close" onClick={onClose} className="text-slate-500 transition-colors hover:text-slate-50">
                <X className="h-5 w-5" />
              </button>
            </div>

            {mode === "forgot" ? (
              forgotSent ? (
                <div data-testid="forgot-success" className="mt-6 text-center">
                  <p className="font-display text-lg font-bold uppercase text-slate-50">Check your inbox</p>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">
                    If that email has a GuardGig28 account, a reset link is on its way.
                    It works once and expires in 1 hour.
                  </p>
                  <button
                    data-testid="forgot-back-btn"
                    onClick={() => { setMode("login"); setForgotSent(false); setError(""); }}
                    className="mt-6 font-mono-tag text-[10px] text-amber-500 underline underline-offset-4 hover:text-amber-400"
                  >
                    Back to sign in
                  </button>
                </div>
              ) : (
                <form onSubmit={submitForgot} className="mt-6 space-y-4" data-testid="forgot-form">
                  <p className="text-sm text-slate-400">Enter your account email and we'll send you a reset link.</p>
                  <input
                    data-testid="forgot-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email"
                    required
                    className={inputCls}
                  />
                  {error && <p data-testid="forgot-error" className="text-sm text-red-400">{error}</p>}
                  <button
                    data-testid="forgot-submit-btn"
                    type="submit"
                    disabled={loading}
                    className="flex h-12 w-full items-center justify-center gap-2 rounded-md bg-amber-500 font-display text-base font-bold uppercase text-[#07090e] transition-all hover:bg-amber-400 disabled:opacity-50"
                  >
                    {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                    Send reset link
                  </button>
                  <button
                    data-testid="forgot-cancel-btn"
                    type="button"
                    onClick={() => { setMode("login"); setError(""); }}
                    className="w-full text-center font-mono-tag text-[10px] text-slate-500 underline underline-offset-4 hover:text-slate-300"
                  >
                    Back to sign in
                  </button>
                </form>
              )
            ) : (
            <>
            <div className="mt-6 flex gap-2">
              {["login", "signup"].map((m) => (
                <button
                  key={m}
                  data-testid={`auth-tab-${m}`}
                  onClick={() => { setMode(m); setError(""); }}
                  className={`flex-1 rounded-md px-4 py-2.5 font-display text-sm font-bold uppercase transition-all ${
                    mode === m ? "bg-amber-500 text-[#07090e]" : "border border-white/10 text-slate-400 hover:text-slate-50"
                  }`}
                >
                  {m === "login" ? "Sign in" : "Sign up"}
                </button>
              ))}
            </div>

            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="auth-form">
              {mode === "signup" && (
                <input
                  data-testid="auth-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Full name"
                  required
                  className={inputCls}
                />
              )}
              <input
                data-testid="auth-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                required
                className={inputCls}
              />
              <div className="relative">
                <input
                  data-testid="auth-password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === "signup" ? "Password (min 8 characters)" : "Password"}
                  required
                  minLength={mode === "signup" ? 8 : 1}
                  className={`${inputCls} pr-11`}
                />
                <button
                  data-testid="auth-password-toggle"
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500 transition-colors hover:text-amber-500"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" strokeWidth={1.75} /> : <Eye className="h-4 w-4" strokeWidth={1.75} />}
                </button>
              </div>
              {mode === "login" && (
                <button
                  data-testid="forgot-password-link"
                  type="button"
                  onClick={() => { setMode("forgot"); setForgotSent(false); setError(""); }}
                  className="w-full text-right font-mono-tag text-[10px] text-slate-500 underline underline-offset-4 transition-colors hover:text-amber-500"
                >
                  Forgot password?
                </button>
              )}
              {error && <p data-testid="auth-error" className="text-sm text-red-400">{error}</p>}
              <button
                data-testid="auth-submit-btn"
                type="submit"
                disabled={loading}
                className="flex h-12 w-full items-center justify-center gap-2 rounded-md bg-amber-500 font-display text-base font-bold uppercase text-[#07090e] transition-all hover:bg-amber-400 disabled:opacity-50"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {mode === "login" ? "Sign in" : "Create account"}
              </button>
            </form>

            <div className="my-5 flex items-center gap-3">
              <span className="h-px flex-1 bg-white/10" />
              <span className="font-mono-tag text-[9px] text-slate-600">or</span>
              <span className="h-px flex-1 bg-white/10" />
            </div>

            {GOOGLE_ENABLED ? (
              <div data-testid="auth-google-btn" className="flex justify-center">
                <GoogleLogin
                  theme="filled_black"
                  size="large"
                  width="352"
                  text="continue_with"
                  onSuccess={googleIdSuccess}
                  onError={() => toast.error("Google sign-in failed. Try email instead.")}
                />
              </div>
            ) : (
            <button
              data-testid="auth-google-btn"
              onClick={googleLogin}
              className="flex h-12 w-full items-center justify-center gap-3 rounded-md border border-white/15 bg-white/5 text-sm font-semibold text-slate-50 transition-all hover:border-amber-500/40 hover:bg-white/10"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#EA4335" d="M12 5.04c1.62 0 3.06.56 4.2 1.64l3.12-3.12C17.46 1.8 14.96.72 12 .72 7.44.72 3.56 3.42 1.7 7.32l3.66 2.84C6.24 7.22 8.88 5.04 12 5.04z"/><path fill="#4285F4" d="M23.28 12.26c0-.92-.08-1.6-.26-2.3H12v4.18h6.44c-.13 1.08-.83 2.7-2.39 3.8l3.56 2.76c2.14-1.97 3.67-4.88 3.67-8.44z"/><path fill="#FBBC05" d="M5.37 14.1A7.1 7.1 0 0 1 5 12c0-.73.13-1.44.35-2.1L1.7 7.32A11.32 11.32 0 0 0 .72 12c0 1.82.44 3.56 1.22 5.1l3.43-3z"/><path fill="#34A853" d="M12 23.28c3.04 0 5.6-1 7.46-2.72l-3.56-2.76c-.95.66-2.23 1.12-3.9 1.12-3.12 0-5.76-2.18-6.64-5.14l-3.66 2.82c1.86 3.9 5.74 6.68 10.3 6.68z"/></svg>
              Continue with Google
            </button>
            )}
            </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
